import { useMemo } from 'react'

import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { getIsAccountingPageAccessible } from 'hooks/utils/isAccountingPageAccessible'
import useIsLegalEntityRootOrg from 'hooks/utils/useIsLegalEntityRootOrg'
import useIsVoucherEnabled from 'hooks/utils/useIsVoucherEnabled'
import { useIsTopLevelOrg } from 'layouts/app/useIsTopLevelOrg'

import {
  ALL_ROUTES,
  GRAND_CANYON_ROUTES,
  MAPPINGS_ROUTE,
  ACCOUNTING_ROUTE,
  NAVBAR_ROUTES,
  RULES_ROUTE,
  CONTACTS_ROUTE,
  CONTACTS_UNKNOWN_ADDRESSES_ROUTE,
  CONTACTS_SAVED_ROUTE,
  SOURCES_ROUTE,
  PMA_ROUTE,
  CASES_ROUTE,
  Route,
  DASHBOARD_ROUTE,
  VOUCHERS_ROUTE,
  VOUCHER_DETAILS_ROUTE,
  VOUCHER_DETAILS_TRANSACTIONS_ROUTE,
  VOUCHER_DETAILS_OVERVIEW_ROUTE,
} from './routes'

const NAVBAR_ROUTES_SET = new Set(NAVBAR_ROUTES.map((route) => route.name))

/**
 * Returns the route objects that should be used to construct
 * the React Location route switch and the top-level navbar component.
 */
const useRoutes = () => {
  const {
    enableGrandCanyonErpSync,
    isAccountingPageVisible,
    isCustomErpMappingsEnabled,
    isSourcesRouteHidden,
    showRulesPageForGcOrg,
    showContactsPageForGcOrg,
    isPrincipalMarketAnalysisVisible,
    isPrincipalMarketAnalysisEnabled,
    isPublicSector,
  } = useFeatureFlags()
  const { isLegalEntityRootOrg } = useIsLegalEntityRootOrg()
  const { isParentOrg } = useIsTopLevelOrg()
  const { isVoucherEnabled } = useIsVoucherEnabled()

  const shouldShowDashboard = !isPublicSector || !isParentOrg

  const allRoutes = useMemo(() => {
    let routes = [...ALL_ROUTES]
    const gcFlaggedRoutes = [] as Route[]

    if (showRulesPageForGcOrg) {
      gcFlaggedRoutes.push(RULES_ROUTE)
    }

    if (
      getIsAccountingPageAccessible({
        isAccountingPageVisible,
        isCustomErpMappingsEnabled,
        enableGrandCanyonErpSync,
        isPublicSector,
      })
    ) {
      gcFlaggedRoutes.push(ACCOUNTING_ROUTE)
    }

    if (enableGrandCanyonErpSync && !isPublicSector) {
      gcFlaggedRoutes.push(MAPPINGS_ROUTE)
    }

    if (showContactsPageForGcOrg) {
      gcFlaggedRoutes.push(CONTACTS_ROUTE, CONTACTS_SAVED_ROUTE, CONTACTS_UNKNOWN_ADDRESSES_ROUTE)
    }

    if (isPublicSector) {
      gcFlaggedRoutes.push(CASES_ROUTE)
    }

    routes = routes.filter((r) => [...GRAND_CANYON_ROUTES, ...gcFlaggedRoutes].includes(r))

    // Show PMA route and make it enabled/disabled based on feature flags
    if (isPrincipalMarketAnalysisVisible) {
      routes.push({ ...PMA_ROUTE, isDisabled: !isPrincipalMarketAnalysisEnabled })
    }

    if (isVoucherEnabled) {
      routes.push(
        VOUCHERS_ROUTE,
        VOUCHER_DETAILS_ROUTE,
        VOUCHER_DETAILS_OVERVIEW_ROUTE,
        VOUCHER_DETAILS_TRANSACTIONS_ROUTE
      )
    }

    // If we are in the root org of a legal entity, we want to hide the sources route
    if (isSourcesRouteHidden || isLegalEntityRootOrg) {
      routes = routes.filter((r) => r !== SOURCES_ROUTE)
    }

    if (!shouldShowDashboard) {
      routes = routes.filter((r) => r !== DASHBOARD_ROUTE)
    }

    if (!isPublicSector || !isParentOrg) {
      routes = routes.filter((r) => r !== CASES_ROUTE)
    }

    return routes
  }, [
    showRulesPageForGcOrg,
    enableGrandCanyonErpSync,
    isAccountingPageVisible,
    isCustomErpMappingsEnabled,
    isPublicSector,
    showContactsPageForGcOrg,
    isPrincipalMarketAnalysisVisible,
    isSourcesRouteHidden,
    isLegalEntityRootOrg,
    shouldShowDashboard,
    isParentOrg,
    isPrincipalMarketAnalysisEnabled,
    isVoucherEnabled,
  ])

  const navbarRoutes = useMemo(
    () => allRoutes.filter((r) => NAVBAR_ROUTES_SET.has(r.name)),
    [allRoutes]
  )

  return { navbarRoutes, allRoutes }
}

export default useRoutes
